import { Circ, Expo, TimelineLite } from "gsap";
import * as HLF from "html-living-framework";
import * as PIXI from "pixi.js";
import { Application } from "../Application";
import {Ease} from "../core/Animatable";
import {Container} from "../core/Container";
import {array} from "../core/Reflection";
import {Sprite} from "../core/Sprite";
import LightsOutGameModel from "../data/LightsOutGameModel";
import { LightsOutObjectType } from "../data/LightsOutObjectType";
import * as VO from "../data/VO";
import { AnimatedLightsOutObject } from "../gameobjects/AnimatedLightsOutObject";
import { Button } from "../gameobjects/Button";
import { LightsOutObject } from "../gameobjects/LightsOutObject";
import { StaticLightsOutObject } from "../gameobjects/StaticLightsOutObject";
import {LightsOutElmoKitchenLayout} from "../layout/LightsOutElmoKitchenLayout";
import {AbstractGameState} from "./AbstractGameState";
import { AbstractLightsOutGameState } from "./AbstractLightsOutGameState";

export class LightsOutElmoKitchenScreen extends AbstractLightsOutGameState {

    public static readonly NAME = "LightsOutElmoKitchenScreen";

    public readonly bg = this.addChild(new Sprite());
    public readonly pots = this.addChild(new AnimatedLightsOutObject({
        type: LightsOutObjectType.EasterEgg,
        spriteSheet: "lightsout-kitchen-1",
        frames: 4,
    }));
    public readonly cabinetLighting = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.Light}));
    public readonly cabinetLightSwitch = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.Light}));
    public readonly fan = this.addChild(new AnimatedLightsOutObject({
        type: LightsOutObjectType.Light,
        spriteSheet: "lightsout-kitchen-1",
        frames: 6,
        splitFrame: 2,
    }));
    public readonly cookieJar = this.addChild(new AnimatedLightsOutObject({
        type: LightsOutObjectType.EasterEgg,
        spriteSheet: "lightsout-kitchen-1",
        frames: 8,
    }));
    public readonly curtains = this.addChild(new AnimatedLightsOutObject({
        type: LightsOutObjectType.EasterEgg,
        spriteSheet: "lightsout-kitchen-1",
        frames: 6,
        splitFrame: 4,
        toggle: true,
    }));
    public readonly fanSwitch = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.Electricity}));
    public readonly faucet = this.addChild(new AnimatedLightsOutObject({
        type: LightsOutObjectType.Water,
        spriteSheet: "lightsout-kitchen-1",
        frames: 6,
    }));
    public readonly fridgeTop = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.Fridge}));
    public readonly fridgeBottom = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.Fridge}));
    public readonly wallLight = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.Light}));
    public readonly wallLightSwitch = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.Light}));
    public readonly walllightGlow = this.addChild(new Sprite());
    public readonly cabinetlightingGlow = this.addChild(new Sprite());
    public readonly fanGlow = this.addChild(new Sprite());
    protected analyticsScreenName: string = "5d Lights Out_Kitchen Screen";

    protected readonly roomID = "Kitchen";
    protected readonly roomNameVO = VO.LO_ROOM03;

    protected _subscriptions: any[] = [];

    protected itemsToTurnOff: LightsOutObject[] = [
        this.faucet,
        this.fridgeBottom,
        this.fridgeTop,
        this.cabinetLightSwitch,
        this.fanSwitch,
        this.wallLightSwitch,
    ];

    public init(pSize: PIXI.Point, pData: {}) {
        LightsOutElmoKitchenLayout.call(this);
        super.init(pSize, pData);

        this.fridgeTop.lightsOutSFX = "lights-out-fridge-door";
        this.fridgeBottom.lightsOutSFX = "lights-out-fridge-door";
        this.wallLightSwitch.lightsOutSFX = "lights-out-light-switch";
        this.wallLight.lightsOutSFX = "lights-out-light-switch";
        this.cabinetLightSwitch.lightsOutSFX = "lights-out-light-switch";
        this.cabinetLighting.lightsOutSFX = "lights-out-light-switch";
        this.fan.lightsOutSFX = "lights-out-light-switch";
        this.fanSwitch.lightsOutSFX = "lights-out-light-switch";
        this.faucet.lightsOutSFX = "lights-out-tap-drip";
        this.pots.clickSfx = "lights-out-pots";
        this.pots.clickSfxCanOverlap = false;
        this.cookieJar.clickSfx = "lights-out-cookie-jar";
        this.curtains.clickSfx = "lights-out-curtains";

        this.makeObjectClickable(this.faucet);
        this.makeObjectClickable(this.fridgeBottom);
        this.makeObjectClickable(this.fridgeTop);
        this.makeObjectClickable(this.pots);
        this.makeObjectClickable(this.curtains);
        this.makeObjectClickable(this.cookieJar);
        this.makeSwitch(this.cabinetLightSwitch, this.cabinetLighting);
        this.makeSwitch(this.fanSwitch, this.fan);
        this.makeSwitch(this.wallLightSwitch, this.wallLight);

        // @ts-ignore
        this.cabinetLighting.hoverScale = 1;
        // @ts-ignore
        this.cabinetLighting.downScale = 1;

        // randomize objects
        HLF.ArrayUtils.shuffle(this.itemsToTurnOff);

        // move already played objects to end of array
        this.itemsToTurnOff.sort((a, b) => LightsOutGameModel.rooms.Kitchen.itemsLeftOn.indexOf(a.name));

        if (LightsOutGameModel.rooms.Bathroom.completed && LightsOutGameModel.rooms.Bathroom.itemsLeftOn.length) {
            // if previously played bathroom this round
            // but faucet was not one of the items left on
            if (LightsOutGameModel.rooms.Bathroom.itemsLeftOn.indexOf("sink") < 0) {
                console.log ("must leave kitchen faucet on because bathroom sink was not left on this round");
                this.itemsToTurnOff.sort((a, b) => a === this.faucet ? -1 : 1);
            }
            else {
                // offset the array by one so it's not just flip flopping between two sets of three
                this.itemsToTurnOff.unshift(this.itemsToTurnOff.pop()!);
            }
        }
        else {
            console.log ("must leave kitchen faucet on because bathroom has not been played yet this round");
            this.itemsToTurnOff.sort((a, b) => a === this.faucet ? -1 : 1);
        }

        let didLeaveFridgeOpen = false;
        let didLeaveARightSwitchOn = false;

        let itemsLeftOn = 0;

        LightsOutGameModel.rooms.Kitchen.itemsLeftOn = [];

        // disable all but three of them
        for (let i = 0; i < this.itemsToTurnOff.length; i++) {
            if (itemsLeftOn < 3) {
                // only leave top or bottom of fridge open but not both
                if (this.itemsToTurnOff[i].objectType === "refrigerator") {
                    // only leave top or bottom of fridge open but not both
                    if (didLeaveFridgeOpen) {
                        this.itemsToTurnOff[i].TurnOn({animate: false}); // hacky fix for lights glow
                        this.itemsToTurnOff[i].TurnOff({animate: false});
                    }
                    // whichever piece of the fridge we leave open goes on top
                    else {
                        didLeaveFridgeOpen = true;
                        HLF.PixiUtils.sendToFront(this.itemsToTurnOff[i]);
                        this.itemsToTurnOff[i].TurnOn();
                        this.itemsToTurnOff[i].Highlight();
                        itemsLeftOn++;
                        LightsOutGameModel.rooms.Kitchen.itemsLeftOn.push(this.itemsToTurnOff[i].name);
                    }
                }
                // only leave fan switch or cabinet lights switch on, but not both
                else if (this.itemsToTurnOff[i] === this.fanSwitch || this.itemsToTurnOff[i] === this.cabinetLightSwitch) {
                    if (didLeaveARightSwitchOn) {
                        this.itemsToTurnOff[i].TurnOn({animate: false}); // hacky fix for lights glow
                        this.itemsToTurnOff[i].TurnOff({animate: false});
                    }
                    else {
                        didLeaveARightSwitchOn = true;
                        this.itemsToTurnOff[i].TurnOn();
                        this.itemsToTurnOff[i].Highlight();
                        itemsLeftOn++;
                        LightsOutGameModel.rooms.Kitchen.itemsLeftOn.push(this.itemsToTurnOff[i].name);
                    }
                }
                else {
                    this.itemsToTurnOff[i].TurnOn();
                    this.itemsToTurnOff[i].Highlight();
                    itemsLeftOn++;
                    LightsOutGameModel.rooms.Kitchen.itemsLeftOn.push(this.itemsToTurnOff[i].name);
                }
            }
            else {
                this.itemsToTurnOff[i].TurnOn({animate: false}); // hacky fix for lights glow
                this.itemsToTurnOff[i].TurnOff({animate: false});
            }
        }

        HLF.PixiUtils.sendToFront(this.homeButton);
        HLF.PixiUtils.sendToFront(this.elmo);
    }

    public animateIn(pOnComplete: () => void) {
        super.animateIn(pOnComplete);
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
        this.position.set((pSize.x - 1664) / 2, 0);
    }

    public update(pDeltaTime: number) {
        super.update(pDeltaTime);
    }

    public destroy() {
        super.destroy();
        for (const subscription of this._subscriptions) {
            PubSub.unsubscribe(subscription);
        }
    }
}
